import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type Spritesheet,
} from "pixi.js";
import {
  BOARD_SIZE,
  idx,
  isCore,
  type Coord,
  type UnitKind,
  type Layout,
  type TileKind,
  type TowerKind,
  TOWER_KINDS,
  type Frame,
} from "@empire/core-sim";
import palette from "../ui/theme/palette.json";
import { TOWER_GLYPH, UNIT_RADIUS, UNIT_SHAPE, UNIT_TINT } from "../ui/theme/glyphs.js";
import { fxState, gait, interpolate, type FxState } from "./fx.js";
import { bodyMotion, hpColour, shotStyle, UNIT_SCALE } from "./look.js";
import { towerScale, TROOP_SIZE } from "./presentation.js";
import { managePresentation, renderResolution, decorativeBudget } from "../lib/graphics.js";
import { ART_SCALE, frame, loadBoardArt, loadUnitArt } from "./art.js";
import { motionAt, motionTracks, type MotionTracks } from "./motion.js";
import { BattleAudioCursor } from "./battleAudio.js";
import { idleBreath, towerAction, towerActions, unitAction } from "./actionMotion.js";
import { playSound } from "../lib/sound.js";
import { roadFrame } from "./road.js";
import { loadBattlefield, type Battlefield } from "./battlefield.js";
import { loadWorldMaterial } from "./worldLandscape.js";
import { LandscapeBackdrop } from "./landscapeBackdrop.js";
import { TILE_H, TILE_W, ISO, TOP, type Point, type Projection } from "./iso.js";

/**
 * The defence board.
 *
 * Draws a Layout and reports which tile the player is touching. It knows
 * nothing about the rules — the editor owns those, and asks core-sim. This view
 * only turns a Layout into pixels and pixels back into a coordinate.
 *
 * Performance shape (S2-1: 60 fps, under 400 draw calls on a mid-range phone):
 * the ground is ONE Graphics object holding all 144 diamonds, because 144
 * separate objects would be 144 draw calls before anything is even built on
 * them. Only tiles that stand up — road blocks, barricades, towers, the core —
 * get their own sprite, and only those need depth sorting.
 */

const TILE_FILL: Record<TileKind, number> = {
  empty: hex(palette["bg-raised"]),
  road: hex(palette.road),
  mud: hex(palette.mud),
  barricade: hex(palette.wood),
  tower_slot: hex(palette.iron),
  core: hex(palette.gold),
};

/** Blocks are drawn as a raised slab; this is how tall each kind stands. */
const TILE_HEIGHT: Record<TileKind, number> = {
  empty: 0,
  road: 0,
  mud: 0,
  barricade: 14,
  tower_slot: 10,
  core: 18,
};

/**
 * Placeholder colours for the five tower types, until real art in Phase 2.
 *
 * Chosen so a defender can tell them apart at a glance on a small screen
 * rather than for any aesthetic reason: warm for damage, cold for control.
 */
const TOWER_TINT: Record<TowerKind, number> = {
  arrow: hex(palette.grain),
  ballista: hex(palette.wood),
  brazier: hex(palette.clay),
  frost: 0x6fb6d8,
  cannon: hex(palette.iron),
};

/** How far a built tower stands above its slot. */
const TOWER_HEIGHT = 22;

function hex(css: string): number {
  return Number.parseInt(css.replace("#", ""), 16);
}

/** Blend two colours. Used for the frost tint and the white flash of a hit. */
function mix(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round(((a >> 16) & 0xff) + (((b >> 16) & 0xff) - ((a >> 16) & 0xff)) * k);
  const g = Math.round(((a >> 8) & 0xff) + (((b >> 8) & 0xff) - ((a >> 8) & 0xff)) * k);
  const bl = Math.round((a & 0xff) + ((b & 0xff) - (a & 0xff)) * k);
  return (r << 16) | (g << 8) | bl;
}

/**
 * A unit's outline, tilted by its walk.
 *
 * Returns null for a circle, which has no visible rotation — those are drawn as
 * an ellipse instead and lean by breathing rather than by tilting.
 */
function shapePoints(
  shape: string,
  cx: number,
  cy: number,
  sx: number,
  sy: number,
  tilt: number
): number[] | null {
  const unit: [number, number][] =
    shape === "square"
      ? [
          [-1, -1],
          [1, -1],
          [1, 1],
          [-1, 1],
        ]
      : shape === "triangle"
        ? [
            [0, -1],
            [1, 1],
            [-1, 1],
          ]
        : shape === "diamond"
          ? [
              [0, -1],
              [1, 0],
              [0, 1],
              [-1, 0],
            ]
          : [];
  if (unit.length === 0) return null;

  const cos = Math.cos(tilt);
  const sin = Math.sin(tilt);
  const out: number[] = [];
  for (const [ux, uy] of unit) {
    const x = ux * sx;
    const y = uy * sy;
    out.push(cx + x * cos - y * sin, cy + x * sin + y * cos);
  }
  return out;
}

function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

export interface BoardCallbacks {
  /** Fires on press and again on every new tile a drag crosses. */
  onPaint?: (at: Coord) => void;
  onPaintEnd?: () => void;
  onHover?: (at: Coord | null) => void;
  /** Held on one tile without dragging. Used to convert a tile in place. */
  onLongPress?: (at: Coord) => void;
  /**
   * A tap that was not a drag, a hold or a pinch (S4-5).
   *
   * `unitId` is the replay unit under the finger when there is one, because on
   * a board where units walk over towers "what did I just tap" has two answers
   * and the moving one is almost always the one meant.
   */
  onTap?: (at: Coord | null, unitId: number | null) => void;
}

/** How far a finger may travel and still count as a tap rather than a drag. */
const TAP_SLOP = 10;
/** How close to a unit's centre a tap has to land, in screen pixels. */
const TAP_REACH = 16;

/** What a selected tower covers, in tiles. */
export interface RangeRing {
  readonly range: number;
  readonly minRange: number;
}

/** How long a press must be held before it counts as a long-press. */
const LONG_PRESS_MS = 450;

/**
 * How far in you can zoom, as a multiple of the board's natural pixel size.
 * Past this the tiles are just big; there is nothing more to see.
 */
const MAX_SCALE = 3.5;

/** How close to the screen edge a painting finger starts scrolling, in CSS px. */
const EDGE_ZONE = 44;
/** Fastest edge scroll, in CSS px per frame. */
const EDGE_SPEED = 7;

/** Two fingers must move this far apart before it counts as a pinch. */
const PINCH_SLOP = 6;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export class BoardView {
  readonly app: Application;

  private readonly world = new Container();
  private readonly ground = new Graphics();
  /**
   * The ground as pictures (Kenney CC0 art): one sprite per tile, reused
   * between redraws, all from one atlas texture so they batch into a single
   * draw call like the Graphics they replace. Depth-sorted, because the tiles
   * have visible sides.
   */
  private readonly groundArt = new Container();
  private readonly groundSprites: Sprite[] = [];
  private battlefield: Battlefield | null = null;
  private readonly editorGrid = new Graphics();
  private replaying = false;
  private readonly audio = new BattleAudioCursor();
  private readonly art: Spritesheet | null;
  private readonly rings = new Graphics();
  private readonly blocks = new Container();
  private readonly overlay = new Graphics();
  /** Its own layer so the pulse can animate alpha without redrawing. */
  private readonly invalid = new Graphics();
  /**
   * Replay units: health bars, bursts, and the shapes when there is no art.
   * One Graphics for the lot, forty units, one draw call.
   */
  private readonly units = new Graphics();
  /**
   * Replay units as pictures (unit art, Sep 2026): one pooled sprite per unit
   * id, all from one atlas, so they still batch into a single draw call.
   */
  private readonly unitLayer = new Container();
  private readonly unitSprites = new Map<number, Sprite>();
  /** Which way each unit is walking, from its last two positions. */
  private motion: MotionTracks = new Map();
  private unitArt: Spritesheet | null = null;
  private readonly reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  /** Tiles the viewer has not earned the right to see (S4-3). */
  private readonly fog = new Graphics();
  /** One letter per built tower (S6-3). Its own layer so it is never occluded. */
  private readonly glyphs = new Container();
  /** Projectiles in flight (S4-2). One Graphics: a handful of dots, one call. */
  private readonly shots = new Graphics();
  /** Ground shadows under the units: what stops them floating over the board. */
  private readonly shadows = new Graphics();
  /** Impact rings, debris, smoke, muzzle flashes. One Graphics, one call. */
  private readonly effects = new Graphics();
  /** Floating damage numbers, pooled so a busy battle does not allocate. */
  private readonly pops = new Container();
  private readonly popPool: Text[] = [];
  /** "Tap here": a pulsing marker the tutorial points with (see setHint). */
  private readonly hint = new Graphics();
  private hintAt: Coord | null = null;
  /** Barricade blocks by tile, so a fallen one can collapse during a replay. */
  private readonly barricadeSprites = new Map<number, Container>();
  /**
   * The towers, one sprite each, so a recoil is a change of y rather than a
   * redraw of the whole board. Keyed by tile index — which is also what the
   * fx stream names a tower by.
   */
  private readonly towerSprites = new Map<
    number,
    {
      block: Container;
      glyph: Text;
      baseY: number;
      muzzle: Point;
      mechanism?: Sprite;
      rest?: Point;
    }
  >();
  /** id -> unit kind for the replay being drawn, so dots can be told apart. */
  private unitKinds: readonly UnitKind[] = [];

  private layout: Layout;
  private invalidTiles: readonly Coord[] = [];
  private selected: Coord | null = null;
  private selectedRange: RangeRing | null = null;
  private hovered: Coord | null = null;
  /** Where an armed tower will land when the finger lifts. */
  private pendingAt: Coord | null = null;
  private painting = false;
  private lastPainted: string | null = null;
  private callbacks: BoardCallbacks = {};
  private destroyed = false;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /** Live pointers, by id. Two or more means a pan/zoom gesture, not painting. */
  private readonly pointers = new Map<number, Point>();
  private gesture: { distance: number; midpoint: Point } | null = null;
  private onView: ((zoom: number) => void) | null = null;
  /** Where each unit was last drawn, in world coordinates, so a tap can find it. */
  private readonly drawnAt = new Map<number, Point>();
  /** Where the painting finger is now, for scrolling the board under it. */
  private paintPoint: Point | null = null;
  /** Where a single-finger press started, for telling a tap from a drag. */
  private pressedAt: Point | null = null;
  private dragged = false;
  private longPressFired = false;
  /** The unit or tile the viewer is inspecting (S4-5). */
  private inspecting: { unitId: number | null; tile: number | null } | null = null;

  /** Isometric for watching; straight from above for building (29 Sep 2026). */
  readonly proj: Projection;
  private releasePresentation: (() => void) | null = null;

  /**
   * Standing pictures (towers, barricades) drawn larger from above: a square
   * tile has twice the room of a diamond, and at 44 px a tower drawn at its
   * isometric size was a speck in the corner of its square.
   */
  private get standScale(): number {
    return this.proj.topDown ? 1.35 : 1;
  }

  private constructor(
    app: Application,
    layout: Layout,
    art: Spritesheet | null,
    proj: Projection = ISO
  ) {
    this.app = app;
    this.proj = proj;
    this.layout = layout;
    this.art = art;
    this.groundArt.sortableChildren = true;

    // Depth sorting applies only to the raised blocks; the ground is flat and
    // always underneath.
    this.blocks.sortableChildren = true;
    this.unitLayer.sortableChildren = true;

    // Rings sit above the ground but below the blocks, so a tall barricade
    // still reads as standing in front of the circle it is inside.
    this.world.addChild(this.ground);
    this.world.addChild(this.groundArt);
    this.world.addChild(this.editorGrid);
    // Fog is a cover on the GROUND, under anything standing up. Drawn on top of
    // everything (as it was), the flat diamonds of tiles behind a tower were
    // painted across the tower's face and read as a rendering glitch.
    this.world.addChild(this.fog);
    this.world.addChild(this.rings);
    this.world.addChild(this.blocks);
    this.world.addChild(this.glyphs);
    this.world.addChild(this.overlay);
    this.world.addChild(this.shadows);
    this.world.addChild(this.unitLayer);
    this.world.addChild(this.units);
    this.world.addChild(this.shots);
    this.world.addChild(this.effects);
    this.world.addChild(this.pops);
    this.world.addChild(this.invalid);
    this.world.addChild(this.hint);
    this.app.stage.addChild(this.world);

    this.app.stage.eventMode = "static";
    this.app.stage.hitArea = { contains: () => true };
    this.bindPointer();

    // A steady 1 Hz breath on the offending tiles. Animating the layer's alpha
    // rather than redrawing it keeps this free: no geometry is rebuilt.
    // Drawing a road off the edge of the screen scrolls the board under the
    // finger, the way every drawing app on a phone does. With the editor
    // zoomed in to finger-sized tiles, a long road would otherwise need the
    // player to stop, pinch out, pan and start a new stroke.
    this.app.ticker.add(() => this.edgeScroll());

    // The hint bobs and breathes so it is the first thing the eye finds.
    this.app.ticker.add(() => {
      if (!this.hintAt) return;
      if (this.reducedMotion.matches) {
        this.hint.alpha = 1;
        this.hint.y = 0;
        return;
      }
      const t = this.app.ticker.lastTime / 1000;
      this.hint.alpha = 0.65 + 0.35 * Math.sin(t * Math.PI * 2);
      this.hint.y = Math.abs(Math.sin(t * Math.PI * 1.5)) * -5;
    });

    this.app.ticker.add(() => {
      if (this.invalidTiles.length === 0) return;
      if (this.reducedMotion.matches) {
        this.invalid.alpha = 0.8;
        return;
      }
      const t = this.app.ticker.lastTime / 1000;
      this.invalid.alpha = 0.55 + 0.45 * Math.sin(t * Math.PI * 2);
    });
  }

  /**
   * `topDown` draws the board straight from above, with square tiles — the
   * view for building with a finger. Everything else (battles, replays)
   * keeps the isometric look.
   */
  static async mount(
    host: HTMLElement,
    layout: Layout,
    options: { topDown?: boolean } = {}
  ): Promise<BoardView> {
    const app = new Application();
    await app.init({
      background: palette.bg,
      antialias: true,
      resizeTo: host,
      resolution: renderResolution(),
      autoDensity: true,
      preference: "webgl",
    });
    host.appendChild(app.canvas);

    const [art, units, battlefield, material] = await Promise.all([
      loadBoardArt(),
      loadUnitArt(),
      loadBattlefield(),
      loadWorldMaterial(),
    ]);
    const view = new BoardView(app, layout, art, options.topDown ? TOP : ISO);
    if (material) {
      view.backdrop = new LandscapeBackdrop(material);
      app.stage.addChildAt(view.backdrop.sprite, 0);
    }
    view.unitArt = units;
    // The painted battlefield is an isometric picture; from above the board
    // draws its own flat ground instead (drawGroundTop).
    if (options.topDown) battlefield?.dispose();
    else {
      view.battlefield = battlefield;
      if (battlefield) view.world.addChildAt(battlefield, 2);
    }
    // Dev builds only: let browser tests find a tile on screen whatever the zoom.
    if (import.meta.env.DEV) liveBoards().add(view);
    view.redraw();
    view.fitToScreen();
    app.renderer.on("resize", () => view.reframe());
    view.releasePresentation = managePresentation(app);
    return view;
  }

  on(callbacks: BoardCallbacks): void {
    this.callbacks = callbacks;
    this.editorGrid.visible = !this.replaying && !!(callbacks.onPaint || callbacks.onLongPress);
  }

  /** Told the current zoom whenever it changes, so the UI can show it. */
  onViewChange(fn: (zoom: number) => void): void {
    this.onView = fn;
    fn(this.zoom);
  }

  /** Swap in a new layout and redraw. Called on every edit. */
  setLayout(layout: Layout, invalidTiles: readonly Coord[] = []): void {
    this.layout = layout;
    this.invalidTiles = invalidTiles;
    this.redraw();
  }

  /**
   * Draw one moment of a replay (S4-1, S4-2).
   *
   * Takes the whole frame list and a FRACTIONAL position rather than one frame,
   * for two reasons that turned out to be the same reason. The simulation runs
   * at 10 ticks a second and the screen draws at 60, so positions have to be
   * interpolated or everything moves in ten jumps a second. And an animation —
   * a recoil, a fading corpse, a shot in the air — spans several ticks, so it
   * can only be drawn by a renderer that can look either side of now. Both fall
   * out of "here is the battle, here is the moment".
   *
   * Pass null to clear the board.
   */
  setReplay(frames: readonly Frame[] | null, position = 0, playing = false): void {
    // A render loop can outlive the board by a frame or two when a screen is
    // closed mid-battle, and drawing into a destroyed Graphics throws.
    if (this.destroyed) return;
    for (const cue of this.audio.step(frames, position, playing, (tile) =>
      this.towerSprites.has(tile)
    ))
      playSound(cue);
    this.replaying = !!frames?.length;
    this.glyphs.visible = !this.replaying;
    this.editorGrid.visible =
      !this.replaying && !!(this.callbacks.onPaint || this.callbacks.onLongPress);
    if (!frames || frames.length === 0) {
      this.units.clear();
      this.shadows.clear();
      for (const sprite of this.unitSprites.values()) sprite.visible = false;
      this.motion = new Map();
      this.shots.clear();
      this.effects.clear();
      this.hidePops(0);
      this.clearRecoil();
      this.resetRubble();
      return;
    }
    this.motion = motionTracks(frames);
    const fx = fxState(frames, position);
    this.effects.clear();
    this.drawUnits(interpolate(frames, position), position, fx);
    this.drawShots(fx);
    this.drawBursts(fx);
    this.drawRubble(fx);
    this.drawSilenced(fx, position);
    this.drawTowerLife(fx, position);
    this.drawPops(fx);
    this.applyRecoil(fx, towerActions(frames, position), position);
    this.drawInspection();
  }

  /**
   * Mark what is being inspected (S4-5).
   *
   * A popover that appears without anything on the board changing leaves the
   * viewer checking whether they tapped the thing they meant to.
   */
  setInspecting(unitId: number | null, tile: number | null): void {
    if (this.destroyed) return;
    this.inspecting = unitId === null && tile === null ? null : { unitId, tile };
    this.drawInspection();
  }

  private drawInspection(): void {
    const g = this.units;
    if (!this.inspecting) return;

    const { unitId, tile } = this.inspecting;
    if (unitId !== null) {
      const at = this.drawnAt.get(unitId);
      if (at) {
        g.circle(at.x, at.y, 11);
        g.stroke({ color: hex(palette.gold), width: 2, alpha: 0.95 });
      }
      return;
    }
    if (tile !== null) {
      const x = tile % this.layout.size;
      const y = Math.floor(tile / this.layout.size);
      g.poly(this.proj.outline(x, y));
      g.stroke({ color: hex(palette.gold), width: 2.5, alpha: 0.95 });
    }
  }

  /** The replay unit nearest a screen point, if one is close enough to mean it. */
  private unitNear(world: Point): number | null {
    let best: number | null = null;
    let bestDistance = TAP_REACH;
    for (const [id, at] of this.drawnAt) {
      const distance = Math.hypot(at.x - world.x, at.y - world.y);
      if (distance <= bestDistance) {
        best = id;
        bestDistance = distance;
      }
    }
    return best;
  }

  /** Units, with their walk, their bruises and their last moments. */
  private drawUnits(triples: readonly number[], position: number, fx: FxState): void {
    const g = this.units;
    g.clear();
    this.shadows.clear();
    this.drawnAt.clear();
    for (const sprite of this.unitSprites.values()) sprite.visible = false;

    for (let i = 0; i + 2 < triples.length; i += 3) {
      this.drawUnit(triples[i]!, triples[i + 1]!, triples[i + 2]!, position, fx, false);
    }

    // The dead and the arrived are no longer in the frame — they left the
    // simulation the moment they stopped mattering to it. Drawing them for
    // another few ticks is the difference between a unit dying and a unit
    // blinking out of existence.
    for (const [id, at] of fx.ghosts) {
      this.drawUnit(id, at.x, at.y, position, fx, true);
    }
  }

  private drawUnit(
    id: number,
    milliX: number,
    milliY: number,
    position: number,
    fx: FxState,
    ghost: boolean
  ): void {
    const state = fx.units.get(id);
    const fade = state?.fade ?? 0;
    if (ghost && fade >= 1) return;

    const kind = this.unitKinds[id];
    const g = this.units;
    const p = this.proj.gridToScreen(milliX / 1000, milliY / 1000);
    const pose = motionAt(this.motion, id, position, kind);
    const reduced = this.reducedMotion.matches;
    const casualty = ghost && !state?.reached;
    const stateName = unitAction(state, ghost, pose.moving, reduced);
    const authored =
      kind && this.unitArt && stateName
        ? (frame(this.unitArt, `${kind}_${pose.facing}_${stateName}`) ??
          (casualty
            ? frame(this.unitArt, `${kind}_${pose.facing}_${fade < 0.3 ? "hit" : "fallen"}`)
            : null))
        : null;
    const riggedSequence =
      kind && this.unitArt
        ? frame(
            this.unitArt,
            `${kind}_${pose.facing}_walk_${reduced ? 0 : Math.floor((pose.phase / (Math.PI * 2)) * 12) % 12}`
          )
        : null;
    const sequence =
      riggedSequence ??
      (kind && this.unitArt
        ? frame(this.unitArt, `${kind}_${pose.facing}_${reduced ? 0 : pose.frame}`)
        : null);
    // A little body weight follows the same distance-based cycle as the feet.
    const weight = reduced
      ? { bob: 0, tilt: 0, squash: 1 }
      : sequence && kind
        ? bodyMotion(kind, pose.phase, pose.turn, !ghost && pose.moving && !authored)
        : gait(id, position, !ghost && pose.moving);
    // The 3D bake plants each stance foot. A second whole-sprite bob would lift it.
    const walk = riggedSequence ? { ...weight, bob: 0 } : weight;

    const shape = kind ? UNIT_SHAPE[kind] : "circle";
    const base = (kind ? UNIT_RADIUS[kind] : 5) * UNIT_SCALE;
    const impact = reduced ? 0 : (state?.impact ?? 0);

    // Hit: squashed wide and flat, and knocked back a pixel or two. At phone
    // size a flinch alone is invisible; the squash is what reads as "struck".
    const sx = base * walk.squash * (1 + impact * 0.45);
    const sy = base * (1 / walk.squash) * (1 - impact * 0.35) * (1 - fade * 0.5);
    const cx = p.x;
    const lift = base + 2;
    // The arrived rise into the core; the dead sink into the ground.
    const cy = p.y - lift + walk.bob + (state?.reached ? -fade * 14 : fade * 6);

    this.drawnAt.set(id, { x: cx, y: cy });

    // Shadow on the ground, not bobbing: it is what makes the bob read as a step.
    const shadowAlpha = 0.35 * (1 - fade);
    if (shadowAlpha > 0.02) {
      this.shadows.ellipse(cx, p.y - 1, base * 0.95, base * 0.42);
      this.shadows.fill({ color: 0x000000, alpha: shadowAlpha });
    }

    const tint = kind ? hex(UNIT_TINT[kind]) : hex(palette.clay);
    const frost = state?.frost ?? 0;
    const texture =
      authored ??
      sequence ??
      (kind && this.unitArt ? frame(this.unitArt, `${kind}_${pose.facing}`) : null);
    if (texture) {
      // The picture stands on the unit's square; the shadow stays on the ground.
      let sprite = this.unitSprites.get(id);
      if (!sprite) {
        sprite = new Sprite(texture);
        this.unitSprites.set(id, sprite);
        this.unitLayer.addChild(sprite);
      }
      sprite.texture = texture;
      sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
      sprite.visible = true;
      sprite.position.set(
        cx,
        p.y + walk.bob + (reduced || authored ? 0 : state?.reached ? -fade * 14 : fade * 6)
      );
      sprite.rotation = authored ? 0 : walk.tilt + (reduced || state?.reached ? 0 : fade * 0.65);
      sprite.scale.set(
        ART_SCALE * (kind ? TROOP_SIZE[kind] : 1),
        ART_SCALE *
          (kind ? TROOP_SIZE[kind] : 1) *
          (authored
            ? stateName === "ready"
              ? idleBreath(kind ?? "", id, position, reduced)
              : 1
            : (1 - impact * 0.025) * (1 - (reduced ? 0 : fade * 0.25)))
      );
      // Frost cools the picture; a hit flashes it pale. Never all the way to
      // white, or the unit loses its own colour at the moment it matters.
      sprite.tint = mix(mix(0xffffff, 0x9fd8f0, frost * 0.7), 0xfff4e0, impact * 0.5);
      sprite.alpha = casualty && authored ? 1 - Math.max(0, (fade - 0.65) / 0.35) : 1 - fade;
      // Sorted by depth: a unit nearer the viewer is drawn over one behind it.
      sprite.zIndex = this.proj.depth(milliX, milliY);
      // A ring of the unit's colour at its feet: the same colour as its chip in
      // the planner, readable when the picture itself is small.
      this.shadows.ellipse(cx, p.y - 1, base * 0.95, base * 0.42);
      this.shadows.stroke({ color: tint, width: 1, alpha: 0.22 * (1 - fade) });
    } else {
      const pts = shapePoints(shape, cx, cy, sx, sy, walk.tilt);
      if (pts) g.poly(pts);
      else g.ellipse(cx, cy, sx, sy);
      // Flashing all the way to white would lose the unit's own colour at the
      // exact moment you are looking hardest at it.
      const colour = mix(mix(tint, 0x9fd8f0, frost * 0.55), 0xffffff, impact * 0.7);
      g.fill({ color: colour, alpha: 1 - fade });
      g.stroke({
        color: frost > 0.3 ? 0xcff0ff : hex(palette.bg),
        width: 1.6,
        alpha: 0.9 * (1 - fade),
      });
    }

    // Health bar, only once it has been hurt: a full army of full bars is noise.
    const health = fx.hp.get(id);
    if (!ghost && health !== undefined && health < 1000) {
      const w = Math.max(14, base * 2.6);
      const bx = cx - w / 2;
      const by = sequence ? p.y - (kind === "rider" ? 52 : 48) : cy - base - 7;
      g.rect(bx - 1, by - 1, w + 2, 5);
      g.fill({ color: 0x000000, alpha: 0.7 });
      g.rect(bx, by, (w * health) / 1000, 3);
      g.fill({ color: hpColour(health) });
    }

    // Death: a small burst of fragments flung outward, then gone. Arriving at
    // the core is a gold sparkle instead — same length, opposite meaning.
    if (!reduced && ghost && fade > 0 && fade < 1) {
      const e = this.effects;
      const spread = 4 + fade * (state?.reached ? 12 : 16);
      for (let k = 0; k < Math.ceil(5 * decorativeBudget()); k++) {
        const angle = (k / 7) * Math.PI * 2 + id * 0.7;
        const fx0 = cx + Math.cos(angle) * spread;
        const fy0 =
          cy + Math.sin(angle) * spread * 0.6 + (state?.reached ? -fade * 6 : fade * fade * 10);
        e.circle(fx0, fy0, (state?.reached ? 1.8 : 2.2) * (1 - fade * 0.6));
        e.fill({ color: state?.reached ? hex(palette.gold) : tint, alpha: 1 - fade });
      }
    }
  }

  /** The kind of tower on a tile, if this viewer knows it. */
  private towerKindAt(tile: number): TowerKind | null {
    return this.layout.tiles[tile]?.tower?.kind ?? null;
  }

  /**
   * Projectiles, one look per tower type, so a viewer can tell WHAT is shooting
   * them from the shot alone: arrows are thin streaks, the Ballista a heavy
   * flat bolt, the Cannon a ball on a high arc, the Brazier a fireball, and
   * Frost a beam.
   */
  private drawShots(fx: FxState): void {
    const g = this.shots;
    g.clear();

    for (const shot of fx.shots) {
      const tower = this.towerSprites.get(shot.tile);
      if (!tower) continue; // a tower this viewer never earned the right to see

      const kind = TOWER_KINDS[shot.kind] as TowerKind | undefined;
      const style = shotStyle(kind);
      const from = tower.muzzle;
      const to = this.proj.gridToScreen(shot.toX / 1000, shot.toY / 1000);
      const toY = to.y - 10;
      const at = (t: number): Point => ({
        x: from.x + (to.x - from.x) * t,
        y: from.y + (toY - from.y) * t - Math.sin(t * Math.PI) * style.arc,
      });
      const head = at(shot.t);

      if (style.form === "beam") {
        for (
          let k = 0;
          k < (this.reducedMotion.matches ? 0 : Math.ceil(6 * decorativeBudget()));
          k++
        ) {
          const p = at((shot.t * k) / 6);
          const r = 2 + Math.sin(k * 2 + shot.t * 12);
          g.circle(p.x + Math.sin(k * 3 + shot.t * 9) * 3, p.y - r, r);
          g.fill({ color: 0xb9fff2, alpha: 0.3 });
        }
        g.moveTo(from.x, from.y);
        g.lineTo(head.x, head.y);
        g.stroke({ color: style.colour, width: 3.5, alpha: 0.35 });
        g.moveTo(from.x, from.y);
        g.lineTo(head.x, head.y);
        g.stroke({ color: 0xffffff, width: 1.2, alpha: 0.9 });
        continue;
      }

      if (style.form === "streak") {
        const tail = at(Math.max(0, shot.t - style.length));
        g.moveTo(tail.x, tail.y);
        g.lineTo(head.x, head.y);
        g.stroke({ color: style.colour, width: style.size, alpha: 1, cap: "round" });
        const angle = Math.atan2(head.y - tail.y, head.x - tail.x);
        const length = kind === "ballista" ? 6 : 4;
        g.poly([
          head.x,
          head.y,
          head.x - Math.cos(angle - 0.45) * length,
          head.y - Math.sin(angle - 0.45) * length,
          head.x - Math.cos(angle + 0.45) * length,
          head.y - Math.sin(angle + 0.45) * length,
        ]);
        g.fill({ color: 0xffe5ae });
        continue;
      }

      // Balls: a faint trail, a glow for fire, then the ball itself.
      for (let k = 1; k <= (this.reducedMotion.matches ? 0 : 3); k++) {
        const trail = at(Math.max(0, shot.t - k * 0.09));
        g.circle(trail.x, trail.y, style.size * (1 - k * 0.22));
        g.fill({ color: kind === "cannon" ? 0xb1a58e : style.colour, alpha: 0.28 - k * 0.07 });
      }
      if (style.glow) {
        g.circle(head.x, head.y, style.size * 2);
        g.fill({ color: style.glow, alpha: 0.35 });
      }
      g.circle(head.x, head.y, style.size);
      g.fill({ color: style.colour });
      g.stroke({ color: hex(palette.bg), width: 1, alpha: 0.7 });
      if (style.glow) {
        g.circle(head.x - 1, head.y - 1, style.size * 0.55);
        g.fill({ color: 0xffe7a1, alpha: 0.9 });
      }
    }
  }

  /** Where each shot landed: a ring, sized and coloured by what fired it. */
  private drawBursts(fx: FxState): void {
    const e = this.effects;
    // One Brazier shot hits every unit in its splash, and each hit is its own
    // event. Draw the blast once per shot, not once per unit it caught.
    const blasts = new Set<string>();
    for (const burst of fx.bursts) {
      const kind = burst.tile >= 0 ? this.towerKindAt(burst.tile) : null;
      if (kind === "brazier") {
        const key = `${burst.tile}:${burst.t}`;
        if (blasts.has(key)) continue;
        blasts.add(key);
      }
      const style = shotStyle(kind ?? undefined);
      const p = this.proj.gridToScreen(burst.x / 1000, burst.y / 1000);
      const cy = p.y - 8;
      const grow = 1 - Math.pow(1 - burst.t, 2);
      const alpha = 1 - burst.t;
      if (this.reducedMotion.matches) {
        // Keep the impact readable, without an expanding flash or debris.
        e.ellipse(p.x, p.y - 2, 7, 3);
        e.stroke({ color: style.colour, width: 1.5, alpha: alpha * 0.7 });
        continue;
      }
      if (kind === "brazier") {
        // The Brazier splashes a whole tile: draw the tile-sized blast so a
        // viewer can SEE why the three units beside the target burned too.
        const r = this.proj.rangeEllipse(1); // splash radius: one tile
        e.ellipse(p.x, p.y, r.rx * (0.4 + grow * 0.6), r.ry * (0.4 + grow * 0.6));
        e.fill({ color: style.colour, alpha: 0.28 * alpha });
        e.stroke({ color: style.glow ?? style.colour, width: 2, alpha: 0.8 * alpha });
        for (let k = 0; k < Math.ceil(6 * decorativeBudget()); k++) {
          const angle = (k * Math.PI) / 4 + burst.tile;
          e.circle(
            p.x + Math.cos(angle) * grow * 24,
            p.y + Math.sin(angle) * grow * 10 - grow * 18,
            1.5 + alpha * 2
          );
          e.fill({ color: k % 2 ? 0xffd389 : 0xeb7238, alpha: alpha * 0.8 });
        }
        continue;
      }
      const radius = style.burst * (0.35 + grow * 0.65);
      if (kind === "cannon") {
        // A low earth plume carries the weight; it does not imply splash damage.
        for (let k = 0; k < Math.ceil(4 * decorativeBudget()); k++) {
          const angle = k * 2.4 + burst.tile;
          e.ellipse(
            p.x + Math.cos(angle) * grow * 12,
            p.y - 2 + Math.sin(angle) * grow * 5 - grow * 5,
            3 + grow * 5,
            2 + grow * 3
          );
          e.fill({ color: k % 2 ? 0xc5b18a : 0x9e8c6c, alpha: alpha * 0.28 });
        }
      } else if (kind === "frost") {
        e.ellipse(p.x, cy, radius, radius * 0.5);
        e.stroke({ color: style.colour, width: 1.2, alpha: 0.55 * alpha });
      }
      // Local impact fragments, not an additional damage radius.
      for (let k = 0; k < Math.ceil((kind === "frost" ? 6 : 3) * decorativeBudget()); k++) {
        const angle = k * 2.4 + burst.tile;
        const x = p.x + Math.cos(angle) * radius;
        const y = cy + Math.sin(angle) * radius * 0.5 - Math.sin(burst.t * Math.PI) * 5;
        e.poly([x, y - 3 * alpha, x + 1.8 * alpha, y, x, y + 3 * alpha, x - 1.8 * alpha, y]);
        e.fill({
          color: kind === "frost" ? 0xbffff3 : kind === "cannon" ? 0x9b896b : 0xd6b879,
          alpha: alpha * 0.8,
        });
      }
      if (burst.t < 0.35) {
        e.circle(p.x, cy, radius * 0.45);
        e.fill({
          color: kind === "frost" ? 0xd5fffa : 0xffdda2,
          alpha: 0.55 * (1 - burst.t / 0.35),
        });
      }
    }
  }

  /** Decorative tower energy uses replay time, so pause and reverse scrubbing also freeze it. */
  private drawTowerLife(fx: FxState, position: number): void {
    if (this.reducedMotion.matches) return;
    const e = this.effects;
    for (const [tile, tower] of this.towerSprites) {
      if (fx.silenced.has(tile)) continue;
      const kind = this.towerKindAt(tile);
      if (kind !== "brazier" && kind !== "frost") continue;
      const x = tower.muzzle.x;
      const y = tower.muzzle.y;
      const t = position / 10;
      if (kind === "frost") {
        e.ellipse(x, y, 9 + Math.sin(t * 2 + tile) * 1.5, 4);
        e.stroke({ color: 0x80e8e1, alpha: 0.35, width: 1 });
      }
      for (let k = 0; k < 3; k++) {
        const phase = (t * (kind === "frost" ? 0.35 : 0.7) + k / 3 + tile * 0.13) % 1;
        e.circle(x + Math.sin(phase * 6 + k) * 5, y - phase * 14, (1 - phase) * 1.8);
        e.fill({ color: kind === "frost" ? 0xc3fff7 : 0xffbd64, alpha: 0.7 * (1 - phase) });
      }
    }
  }

  /** Barricades that have fallen: a collapse, then rubble the road runs over. */
  private drawRubble(fx: FxState): void {
    for (const [tile, sprite] of this.barricadeSprites) {
      const t = fx.fallen.get(tile);
      if (t === undefined) {
        sprite.alpha = 1;
        sprite.y = 0;
        continue;
      }
      sprite.alpha = 1 - t * 0.8;
      sprite.y = t * 12;
      if (t < 1 && !this.reducedMotion.matches) {
        const x = tile % this.layout.size;
        const y = Math.floor(tile / this.layout.size);
        const c = this.proj.gridToScreen(x, y);
        const e = this.effects;
        for (let k = 0; k < 9; k++) {
          const angle = (k / 9) * Math.PI * 2 + tile;
          const d = 6 + t * 26;
          e.rect(
            c.x + Math.cos(angle) * d - 2,
            c.y - 10 + Math.sin(angle) * d * 0.5 + t * t * 14 - 2,
            4,
            4
          );
          e.fill({ color: hex(palette.wood), alpha: 1 - t });
        }
        e.circle(c.x, c.y - 8, 10 + t * 22);
        e.fill({ color: 0xd9c9a8, alpha: 0.35 * (1 - t) });
      }
    }
  }

  /** A tower knocked below its offline threshold goes dark and smokes. */
  private drawSilenced(fx: FxState, position: number): void {
    for (const [tile, sprite] of this.towerSprites) {
      const since = fx.silenced.get(tile);
      if (since === undefined) {
        sprite.block.tint = 0xffffff;
        sprite.glyph.alpha = 1;
        continue;
      }
      sprite.block.tint = 0x5a5a5a;
      sprite.glyph.alpha = 0.45;
      const e = this.effects;
      const top = { x: sprite.glyph.x, y: sprite.baseY + 4 };
      for (let k = 0; k < (this.reducedMotion.matches ? 0 : 3); k++) {
        const phase = (position * 0.07 + k / 3) % 1;
        e.circle(top.x + Math.sin((phase + k) * 5) * 3, top.y - phase * 22, 3 + phase * 5);
        e.fill({ color: 0x4a4a4a, alpha: 0.55 * (1 - phase) });
      }
      // The moment it goes down: one flash so the viewer looks at it.
      if (since < 4 && !this.reducedMotion.matches) {
        e.circle(top.x, top.y + 8, 8 + since * 5);
        e.stroke({ color: hex(palette.danger), width: 2.5, alpha: 1 - since / 4 });
      }
    }
  }

  /** Damage numbers rising off whatever was hit. The newest win when crowded. */
  private drawPops(fx: FxState): void {
    // Past a dozen the numbers stop being information and start being noise.
    const MAX = Math.round((this.app.screen.width < 600 ? 6 : 10) * decorativeBudget());
    const list = fx.pops.length > MAX ? fx.pops.slice(fx.pops.length - MAX) : fx.pops;
    const placed: { x: number; y: number; width: number; height: number }[] = [];
    // Keep labels legible when the full board fits a phone. Newest hits get
    // first choice of space; older labels are hidden if all nearby lanes fill.
    const scale = clamp(1 / this.world.scale.x, 0.85, 1.65);
    let used = 0;
    for (const pop of [...list].reverse()) {
      const p = this.proj.gridToScreen(pop.x / 1000, pop.y / 1000);
      const label = `-${pop.amount}`;
      let text = this.popPool[used];
      if (!text) {
        text = new Text({
          text: "",
          style: {
            fontFamily: "system-ui, sans-serif",
            fontSize: 13,
            fontWeight: "700",
            fill: 0xffedc9,
            stroke: { color: 0x24332c, width: 2.5 },
          },
        });
        text.anchor.set(0.5, 1);
        this.popPool.push(text);
        this.pops.addChild(text);
      }
      if (text.text !== label) text.text = label;
      text.scale.set(scale);
      const width = text.width + 6 * scale;
      const height = text.height + 3 * scale;
      let y = p.y - 48 - (this.reducedMotion.matches ? 0 : pop.t * 18);
      let lane = 0;
      const crowded = () =>
        placed.some(
          (other) =>
            Math.abs(p.x - other.x) < (width + other.width) / 2 &&
            Math.abs(y - other.y) < Math.max(height, other.height)
        );
      while (lane < 3 && crowded()) {
        y -= height;
        lane++;
      }
      if (crowded()) continue;
      placed.push({ x: p.x, y, width, height });
      used += 1;
      text.visible = true;
      text.position.set(p.x, y);
      text.alpha = pop.t < 0.6 ? 1 : 1 - (pop.t - 0.6) / 0.4;
    }
    this.hidePops(used);
  }

  private hidePops(from: number): void {
    for (let i = from; i < this.popPool.length; i++) this.popPool[i]!.visible = false;
  }

  private resetRubble(): void {
    for (const sprite of this.barricadeSprites.values()) {
      sprite.alpha = 1;
      sprite.y = 0;
    }
    for (const sprite of this.towerSprites.values()) {
      sprite.block.tint = 0xffffff;
      sprite.glyph.alpha = 1;
    }
  }

  /** Only the mounted weapon recoils; the masonry remains planted. */
  private applyRecoil(fx: FxState, actions: ReadonlyMap<number, number>, position: number): void {
    for (const [tile, sprite] of this.towerSprites) {
      const kind = this.towerKindAt(tile);
      if (!kind) continue;
      const reduced = this.reducedMotion.matches;
      const disabled = fx.silenced.has(tile);
      const action = towerAction(kind, disabled ? undefined : actions.get(tile), reduced);
      const push = action.recoil;
      sprite.block.y = 0;
      sprite.glyph.y = sprite.baseY;
      if (sprite.mechanism && sprite.rest) {
        const magical = kind === "frost" || kind === "brazier";
        const life = reduced || disabled ? 0 : Math.sin(position * 0.25 + tile);
        sprite.mechanism.position.set(
          sprite.rest.x - (magical ? 0 : push),
          sprite.rest.y +
            (magical ? (kind === "frost" ? life * 0.6 - action.charge : 0) : push * 0.35)
        );
        sprite.mechanism.scale.set(
          ART_SCALE * towerScale(kind, this.proj.topDown),
          ART_SCALE *
            towerScale(kind, this.proj.topDown) *
            (kind === "brazier" && !disabled
              ? 1 + life * 0.04 + action.charge * 0.1 + action.flash * 0.18
              : 1)
        );
        sprite.mechanism.alpha = disabled && magical ? 0.25 : 1;
        if (kind === "arrow" && this.art) {
          const texture = frame(this.art, `tower_arrow_fire_${action.frame}`);
          if (texture) sprite.mechanism.texture = texture;
        }
      }
      if (action.charge > 0 && (kind === "frost" || kind === "brazier")) {
        this.effects.circle(sprite.muzzle.x, sprite.muzzle.y, 2 + action.charge * 4);
        this.effects.fill({
          color: kind === "frost" ? 0xb0fff2 : 0xffc26a,
          alpha: action.charge * 0.35,
        });
      }
      // Bow release stays quiet; powder and magic have distinct short flashes.
      if (action.flash > 0 && kind !== "arrow" && kind !== "ballista") {
        this.effects.circle(sprite.muzzle.x, sprite.muzzle.y + push, 2 + action.flash * 3);
        this.effects.fill({
          color: kind === "frost" ? 0xc3fff7 : 0xffd58d,
          alpha: action.flash * 0.65,
        });
      }
    }
  }

  private clearRecoil(): void {
    for (const [tile, sprite] of this.towerSprites) {
      sprite.block.y = 0;
      sprite.glyph.y = sprite.baseY;
      if (sprite.mechanism && sprite.rest) {
        sprite.mechanism.position.copyFrom(sprite.rest);
        const kind = this.towerKindAt(tile);
        if (kind) sprite.mechanism.scale.set(ART_SCALE * towerScale(kind, this.proj.topDown));
        sprite.mechanism.alpha = 1;
        const texture = this.art && frame(this.art, `tower_${this.towerKindAt(tile)}_weapon`);
        if (texture) sprite.mechanism.texture = texture;
      }
    }
  }

  /**
   * Hatch the tiles this viewer never saw (S4-3).
   *
   * Pass the row-major indices that are UNKNOWN, or null to clear. Drawn as a
   * translucent cover rather than by leaving the ground blank, because blank
   * ground is a statement — "there is nothing there" — and fog is the absence
   * of one.
   */
  setFog(unknown: readonly number[] | null): void {
    if (this.destroyed) return;
    const g = this.fog;
    g.clear();
    if (!unknown || unknown.length === 0) return;

    for (const index of unknown) {
      const x = index % this.layout.size;
      const y = Math.floor(index / this.layout.size);
      g.poly(this.proj.outline(x, y));
      g.fill({ color: hex(palette.bg), alpha: 0.82 });
      g.stroke({ color: hex(palette["panel-edge"]), width: 1, alpha: 0.5 });
      // Hatching, so fog reads as "unknown" rather than as plain dark ground.
      const c = this.proj.gridToScreen(x, y);
      for (const k of [-0.5, 0, 0.5]) {
        g.moveTo(
          c.x - this.proj.tileW * 0.18 + k * this.proj.tileW * 0.3,
          c.y + this.proj.tileH * 0.18
        );
        g.lineTo(
          c.x + this.proj.tileW * 0.18 + k * this.proj.tileW * 0.3,
          c.y - this.proj.tileH * 0.18
        );
      }
      g.stroke({ color: hex(palette["ink-dim"]), width: 1, alpha: 0.22 });
    }
  }

  /**
   * One letter on a tower (S6-3).
   *
   * Colour alone does not survive a phone screen or a red-green deficiency, and
   * five tower types that look alike make the defence puzzle unreadable.
   */
  private addGlyph(letter: string, x: number, y: number): Text {
    const text = new Text({
      text: letter,
      style: {
        fontFamily: "monospace",
        fontSize: 13,
        fontWeight: "700",
        fill: hex(palette.bg),
        stroke: { color: hex(palette.ink), width: 3 },
      },
    });
    text.anchor.set(0.5, 1);
    text.position.set(x, y);
    this.glyphs.addChild(text);
    return text;
  }

  /**
   * Tell the board which unit each replay id is, so the dots can be drawn as
   * their own shape and colour. Pass an empty list to fall back to plain dots —
   * which is what a fogged attacker gets, because a recorded position stream
   * carries no plan to derive types from.
   */
  setUnitKinds(kinds: readonly UnitKind[]): void {
    this.unitKinds = kinds;
  }

  /**
   * Point at one square: a filled, pulsing diamond with an arrow above it.
   *
   * The tutorial used the selection outline for this — a thin 2 px stroke that
   * nobody found on a phone. Pass null to clear.
   */
  setHint(at: Coord | null): void {
    if (this.destroyed) return;
    this.hintAt = at;
    const g = this.hint;
    g.clear();
    if (!at) return;
    const gold = hex(palette.gold);
    g.poly(this.proj.outline(at.x, at.y));
    g.fill({ color: gold, alpha: 0.35 });
    g.stroke({ color: gold, width: 3 });
    const c = this.proj.gridToScreen(at.x, at.y);
    const top = c.y - this.proj.tileH / 2 - 10;
    g.poly([c.x - 9, top - 14, c.x + 9, top - 14, c.x, top]);
    g.fill({ color: gold });
    g.stroke({ color: hex(palette.bg), width: 2 });
  }

  /**
   * Outline the square an armed tower will be built on when the finger
   * lifts. Bold and filled, because the finger itself is covering the tile.
   */
  setPending(at: Coord | null): void {
    if (this.destroyed) return;
    this.pendingAt = at;
    this.drawOverlay();
  }

  /** Highlight a slot and draw what its tower covers. */
  setSelection(at: Coord | null, range: RangeRing | null): void {
    this.selected = at;
    this.selectedRange = range;
    this.drawRings();
    this.drawOverlay();
  }

  // --- drawing ------------------------------------------------------------

  private redraw(): void {
    this.drawGround();
    this.drawBlocks();
    this.drawRings();
    this.drawOverlay();
  }

  /** All 144 tiles in a single Graphics: one object, one draw call. */
  private drawGround(): void {
    const g = this.ground;
    g.clear();
    this.editorGrid.clear();
    if (this.proj.topDown) {
      this.groundArt.visible = false;
      this.drawGroundTop();
      return;
    }
    if (this.battlefield) {
      this.groundArt.visible = false;
      this.battlefield.update(this.layout);
      for (let i = 0; i <= this.layout.size; i++) {
        for (const [a, b] of [
          [
            this.proj.gridToScreen(i - 0.5, -0.5),
            this.proj.gridToScreen(i - 0.5, this.layout.size - 0.5),
          ],
          [
            this.proj.gridToScreen(-0.5, i - 0.5),
            this.proj.gridToScreen(this.layout.size - 0.5, i - 0.5),
          ],
        ]) {
          this.editorGrid.moveTo(a!.x, a!.y).lineTo(b!.x, b!.y);
        }
      }
      this.editorGrid.stroke({ color: 0xefe2bd, width: 0.8, alpha: 0.28 });
      this.editorGrid.visible =
        !this.replaying && !!(this.callbacks.onPaint || this.callbacks.onLongPress);
      return;
    }
    if (this.art) {
      this.drawGroundArt(this.art);
      return;
    }

    for (let y = 0; y < this.layout.size; y++) {
      for (let x = 0; x < this.layout.size; x++) {
        const tile = this.layout.tiles[idx(x, y, this.layout.size)];
        const kind = tile?.kind ?? "empty";
        const flat = TILE_HEIGHT[kind] === 0;

        g.poly(this.proj.outline(x, y));
        g.fill({
          color: flat ? TILE_FILL[kind] : shade(TILE_FILL.empty, 0.85),
          alpha: 1,
        });
        g.stroke({ color: hex(palette.grid), width: 1, alpha: 0.55 });
      }
    }
  }

  /** The ground from the atlas: grass, and the right road piece for each road tile. */
  private drawGroundArt(art: Spritesheet): void {
    const size = this.layout.size;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = idx(x, y, size);
        const kind = this.layout.tiles[i]?.kind ?? "empty";
        const road = kind === "road" || kind === "mud" || kind === "barricade";
        const texture =
          frame(art, road ? roadFrame(this.layout, x, y) : "grass") ?? frame(art, "grass");
        if (!texture) continue;

        let sprite = this.groundSprites[i];
        if (!sprite) {
          sprite = new Sprite(texture);
          sprite.scale.set(ART_SCALE);
          const at = this.proj.gridToScreen(x, y);
          sprite.position.set(at.x, at.y);
          sprite.zIndex = this.proj.depth(x, y);
          this.groundSprites[i] = sprite;
          this.groundArt.addChild(sprite);
        }
        if (sprite.texture !== texture) sprite.texture = texture;
        sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 0.5);
        // Mud is the same road, darker and wetter.
        sprite.tint = kind === "mud" ? 0x8a6a48 : road ? 0xf3dfb3 : 0xded2ab;
      }
    }
  }

  /**
   * The ground seen from straight above (29 Sep 2026): square tiles, drawn
   * flat in one Graphics. Road squares join into one ribbon — a border is
   * drawn only on the sides with no road next door — so the maze reads as a
   * path rather than a row of tiles.
   */
  private drawGroundTop(): void {
    const g = this.ground;
    const size = this.layout.size;
    const t = this.proj.tileW;
    const grassA = 0x5d7d3c;
    const grassB = 0x587737;
    const roadFill = 0xc8a46a;
    const roadEdge = 0x8a6a3e;
    const mudFill = 0x6e5334;
    const stone = 0x8c8676;
    const road = (x: number, y: number): boolean =>
      x >= 0 && y >= 0 && x < size && y < size && this.isRoadTile(x, y);

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const kind = this.layout.tiles[idx(x, y, size)]?.kind ?? "empty";
        const c = this.proj.gridToScreen(x, y);
        const x0 = c.x - t / 2;
        const y0 = c.y - t / 2;
        const fill =
          kind === "road" || kind === "barricade"
            ? roadFill
            : kind === "mud"
              ? mudFill
              : kind === "core"
                ? stone
                : (x + y) % 2 === 0
                  ? grassA
                  : grassB;
        g.rect(x0, y0, t, t);
        g.fill({ color: fill });
        if (kind === "mud") {
          // Puddles, so mud is not just "darker road" at a glance.
          g.ellipse(c.x - t * 0.14, c.y + t * 0.08, t * 0.16, t * 0.08);
          g.ellipse(c.x + t * 0.18, c.y - t * 0.16, t * 0.1, t * 0.05);
          g.fill({ color: 0x4a3a26, alpha: 0.8 });
        }
        if (!road(x, y)) continue;
        // Edges only where the road stops.
        const w = 4;
        if (!road(x, y - 1)) g.rect(x0, y0, t, w);
        if (!road(x, y + 1)) g.rect(x0, y0 + t - w, t, w);
        if (!road(x - 1, y)) g.rect(x0, y0, w, t);
        if (!road(x + 1, y)) g.rect(x0 + t - w, y0, w, t);
        g.fill({ color: roadEdge });
      }
    }

    // A thin grid, so a square is a square to aim at — building boards only.
    const half = (size * t) / 2;
    const top = -t / 2;
    for (let i = 0; i <= size; i++) {
      const x = -half + i * t;
      const y = top + i * t;
      this.editorGrid.moveTo(x, top).lineTo(x, top + size * t);
      this.editorGrid.moveTo(-half, y).lineTo(half, y);
    }
    this.editorGrid.stroke({ color: 0xefe2bd, width: 1, alpha: 0.22 });
    this.editorGrid.visible =
      !this.replaying && !!(this.callbacks.onPaint || this.callbacks.onLongPress);
    // A frame around the whole board, so its edge (where gates go) is obvious.
    g.rect(-half, top, size * t, size * t);
    g.stroke({ color: 0x2a3a22, width: 3, alpha: 0.9 });
  }

  /**
   * A standing picture from the atlas, placed on a tile, inside a holder.
   *
   * The holder sits at 0,0 and the picture at its tile, so the recoil and
   * collapse effects — which move a block by setting its y — keep working the
   * same way they did for the flat slabs.
   */
  private artSprite(art: Spritesheet, name: string, gx: number, gy: number): Container | null {
    const base = frame(art, `${name}_base`);
    const texture = base ?? frame(art, name);
    if (!texture) return null;
    const sprite = new Sprite(texture);
    sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
    sprite.scale.set(
      ART_SCALE *
        (name.startsWith("tower_")
          ? towerScale(name.slice(6) as TowerKind, this.proj.topDown)
          : name === "core"
            ? 0.88
            : this.standScale)
    );
    const at = this.proj.gridToScreen(gx, gy);
    // From above, a standing picture's feet go in the lower part of its
    // square, so the square it belongs to is the one it visibly stands in.
    if (this.proj.topDown) at.y += this.proj.tileH * (name === "core" ? 0.42 : 0.3);
    sprite.position.set(at.x, at.y);
    const holder = new Container();
    holder.addChild(sprite);
    const mechanism = frame(art, `${name}_weapon`);
    if (base && mechanism) {
      const weapon = new Sprite(mechanism);
      weapon.anchor.set(mechanism.defaultAnchor?.x ?? 0.5, mechanism.defaultAnchor?.y ?? 0.85);
      weapon.scale.set(ART_SCALE * towerScale(name.slice(6) as TowerKind, this.proj.topDown));
      const mount =
        name === "tower_arrow"
          ? 0.75
          : name === "tower_brazier"
            ? 0.88
            : name === "tower_frost"
              ? 0.65
              : 0.64;
      weapon.position.set(at.x, at.y - sprite.height * mount);
      holder.addChild(weapon);
    }
    return holder;
  }

  /** Towers, barricades, empty slots and the keep, as pictures. */
  private drawBlocksArt(art: Spritesheet): void {
    const size = this.layout.size;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = idx(x, y, size);
        const tile = this.layout.tiles[i];
        const kind = tile?.kind ?? "empty";

        if (kind === "barricade") {
          const fence = this.artSprite(art, "barricade", x, y);
          if (fence) {
            fence.zIndex = this.proj.depth(x, y) + 0.4;
            this.blocks.addChild(fence);
            this.barricadeSprites.set(i, fence);
          }
          continue;
        }
        if (kind !== "tower_slot") continue;

        const name = tile?.tower ? `tower_${tile.tower.kind}` : "slot";
        const sprite = this.artSprite(art, name, x, y);
        if (!sprite) continue;
        sprite.zIndex = this.proj.depth(x, y) + 0.5;
        this.blocks.addChild(sprite);

        if (tile?.tower) {
          const picture = sprite.children[0] as Sprite;
          const mechanism = sprite.children[1] as Sprite | undefined;
          const upper = mechanism ?? picture;
          const top = upper.y - upper.anchor.y * upper.height;
          const glyph = this.addGlyph(TOWER_GLYPH[tile.tower.kind], picture.x, top + 2);
          const lift =
            tile.tower.kind === "arrow" ? 0.27 : tile.tower.kind === "ballista" ? 0.2 : 0.16;
          this.towerSprites.set(i, {
            block: sprite,
            glyph,
            baseY: top + 2,
            mechanism,
            rest: mechanism ? { x: mechanism.x, y: mechanism.y } : undefined,
            muzzle: mechanism
              ? { x: mechanism.x + mechanism.width * 0.32, y: top + mechanism.height * 0.15 }
              : { x: picture.x, y: top + picture.height * lift },
          });
        }
      }
    }

    // The keep stands on the 2x2 core, drawn after the tiles behind it.
    const keep = this.artSprite(art, "core", 5.5, 5.5);
    if (keep) {
      keep.zIndex = this.proj.depth(6, 6) - 0.5;
      this.blocks.addChild(keep);
    }
  }

  /**
   * Anything that stands above the ground gets its own sprite so it can be
   * depth-sorted. On a full board that is at most the road's tower slots, three
   * barricades and the core — a few dozen objects, not 144.
   */
  private drawBlocks(): void {
    this.blocks.removeChildren().forEach((child) => child.destroy());
    this.glyphs.removeChildren().forEach((child) => child.destroy());
    this.towerSprites.clear();
    this.barricadeSprites.clear();

    if (this.art) {
      this.drawBlocksArt(this.art);
      return;
    }

    for (let y = 0; y < this.layout.size; y++) {
      for (let x = 0; x < this.layout.size; x++) {
        const tile = this.layout.tiles[idx(x, y, this.layout.size)];
        const kind = tile?.kind ?? "empty";
        const height = TILE_HEIGHT[kind];
        if (height === 0) continue;

        const block = new Graphics();
        const base = TILE_FILL[kind];
        const points = this.proj.outline(x, y);

        // Left and right faces, then the top — painted darkest to lightest so
        // the slab reads as solid without any lighting maths.
        block.poly([
          points[6]!,
          points[7]!,
          points[4]!,
          points[5]!,
          points[4]!,
          points[5]! - height,
          points[6]!,
          points[7]! - height,
        ]);
        block.fill({ color: shade(base, 0.6) });

        block.poly([
          points[4]!,
          points[5]!,
          points[2]!,
          points[3]!,
          points[2]!,
          points[3]! - height,
          points[4]!,
          points[5]! - height,
        ]);
        block.fill({ color: shade(base, 0.8) });

        block.poly(points.map((v, i) => (i % 2 === 1 ? v - height : v)));
        block.fill({ color: base });
        block.stroke({ color: shade(base, 0.5), width: 1 });

        block.zIndex = this.proj.depth(x, y);
        this.blocks.addChild(block);
        if (kind === "barricade") this.barricadeSprites.set(idx(x, y, this.layout.size), block);

        // A built tower is a second, narrower slab on top of its slot, tinted
        // by type so five of them on a board are still tellable apart. It gets
        // its OWN sprite rather than being painted into the slot: a tower that
        // recoils when it fires (S4-2) then costs one y offset per shot instead
        // of a rebuild of every block on the board.
        if (tile?.tower) {
          const tint = TOWER_TINT[tile.tower.kind];
          const centre = this.proj.gridToScreen(x, y);
          const w = this.proj.tileW * 0.34;
          const h = this.proj.tileH * 0.34;
          const top = centre.y - height - TOWER_HEIGHT;
          const glyph = this.addGlyph(TOWER_GLYPH[tile.tower.kind], centre.x, top - 4);
          const tower = new Graphics();

          tower.poly([
            centre.x - w,
            centre.y - height,
            centre.x,
            centre.y - height + h,
            centre.x,
            top + h,
            centre.x - w,
            top,
          ]);
          tower.fill({ color: shade(tint, 0.62) });

          tower.poly([
            centre.x,
            centre.y - height + h,
            centre.x + w,
            centre.y - height,
            centre.x + w,
            top,
            centre.x,
            top + h,
          ]);
          tower.fill({ color: shade(tint, 0.82) });

          tower.poly([centre.x, top - h, centre.x + w, top, centre.x, top + h, centre.x - w, top]);
          tower.fill({ color: tint });
          tower.stroke({ color: shade(tint, 0.5), width: 1 });

          tower.zIndex = this.proj.depth(x, y) + 0.5;
          this.blocks.addChild(tower);
          this.towerSprites.set(idx(x, y, this.layout.size), {
            block: tower,
            glyph,
            baseY: top - 4,
            muzzle: { x: centre.x, y: top },
          });
        }
      }
    }
  }

  /**
   * The range of the selected tower, as an ellipse rather than a circle —
   * see `rangeEllipse` for why that is the honest shape. The Cannon also gets
   * an inner ring, because it cannot hit what is too close.
   */
  private drawRings(): void {
    const g = this.rings;
    g.clear();
    if (!this.selected || !this.selectedRange) return;

    const centre = this.proj.gridToScreen(this.selected.x, this.selected.y);
    const outer = this.proj.rangeEllipse(this.selectedRange.range);

    g.ellipse(centre.x, centre.y, outer.rx, outer.ry);
    g.fill({ color: hex(palette.gold), alpha: 0.1 });
    g.stroke({ color: hex(palette.gold), width: 2, alpha: 0.85 });

    if (this.selectedRange.minRange > 0) {
      const inner = this.proj.rangeEllipse(this.selectedRange.minRange);
      g.ellipse(centre.x, centre.y, inner.rx, inner.ry);
      g.fill({ color: hex(palette.bg), alpha: 0.35 });
      g.stroke({ color: hex(palette.gold), width: 1, alpha: 0.5 });
    }
  }

  /** Gate marker, hover highlight and any tiles the validator objected to. */
  private drawOverlay(): void {
    const g = this.overlay;
    g.clear();

    const gate = this.layout.gate;
    if (this.isRoadTile(gate.x, gate.y)) {
      g.poly(this.proj.outline(gate.x, gate.y));
      g.stroke({ color: hex(palette.gold), width: 3, alpha: 1 });
    }

    if (this.selected) {
      g.poly(this.proj.outline(this.selected.x, this.selected.y));
      g.stroke({ color: hex(palette.ok), width: 2.5 });
    }

    // Offending tiles live on their own layer so the pulse can animate its
    // alpha without any of this being redrawn.
    const bad = this.invalid;
    bad.clear();
    for (const at of this.invalidTiles) {
      bad.poly(this.proj.outline(at.x, at.y));
      bad.fill({ color: hex(palette.danger), alpha: 0.35 });
      bad.stroke({ color: hex(palette.danger), width: 2 });
    }
    if (this.invalidTiles.length === 0) bad.alpha = 1;

    if (this.pendingAt) {
      const at = this.pendingAt;
      g.poly(this.proj.outline(at.x, at.y));
      g.fill({ color: hex(palette.gold), alpha: 0.45 });
      g.stroke({ color: hex(palette.gold), width: 3.5 });
      // A marker above the tile, where the finger does not cover it.
      const c = this.proj.gridToScreen(at.x, at.y);
      const top = c.y - this.proj.tileH / 2 - 14;
      g.circle(c.x, top - 8, 7);
      g.fill({ color: hex(palette.gold) });
      g.stroke({ color: hex(palette.bg), width: 2 });
    }

    if (this.hovered && !this.pendingAt) {
      g.poly(this.proj.outline(this.hovered.x, this.hovered.y));
      g.fill({ color: hex(palette.ink), alpha: 0.12 });
      g.stroke({ color: hex(palette.ink), width: 1.5, alpha: 0.6 });
    }
  }

  private isRoadTile(x: number, y: number): boolean {
    const kind = this.layout.tiles[idx(x, y, this.layout.size)]?.kind;
    return kind === "road" || kind === "mud" || kind === "barricade";
  }

  // --- input --------------------------------------------------------------

  private bindPointer(): void {
    const stage = this.app.stage;

    stage.on("pointerdown", (event: FederatedPointerEvent) => {
      this.pointers.set(event.pointerId, { x: event.global.x, y: event.global.y });

      // A second finger turns the whole thing into a pan/zoom gesture. Whatever
      // the first finger had started painting is abandoned, because "pinch" and
      // "draw a road" must never happen at once.
      if (this.pointers.size > 1) {
        this.abandonPainting();
        this.gesture = this.readGesture();
        return;
      }

      this.pressedAt = { x: event.global.x, y: event.global.y };
      this.paintPoint = { x: event.global.x, y: event.global.y };
      this.dragged = false;
      this.painting = true;
      this.lastPainted = null;
      this.paintAt(event);

      // Hold on the tile you pressed and it converts instead of painting. Any
      // move to a NEW tile cancels it, so a drag is never mistaken for a hold.
      const pressed = this.tileUnder(event);
      this.clearLongPress();
      this.longPressFired = false;
      // Only armed when somebody is listening. On the replay board nothing
      // converts tiles, and a timer that fires there would turn a slow, careful
      // tap — the only kind anybody makes while inspecting — into a non-event.
      if (pressed && this.callbacks.onLongPress) {
        this.longPressTimer = setTimeout(() => {
          this.longPressTimer = null;
          this.longPressFired = true;
          this.callbacks.onLongPress?.(pressed);
        }, LONG_PRESS_MS);
      }
    });

    stage.on("pointermove", (event: FederatedPointerEvent) => {
      if (this.pointers.has(event.pointerId)) {
        this.pointers.set(event.pointerId, { x: event.global.x, y: event.global.y });
      }

      if (this.pointers.size > 1) {
        this.dragged = true;
        this.updateGesture();
        return;
      }
      if (this.painting) this.paintPoint = { x: event.global.x, y: event.global.y };

      if (
        this.pressedAt &&
        Math.hypot(event.global.x - this.pressedAt.x, event.global.y - this.pressedAt.y) > TAP_SLOP
      ) {
        this.dragged = true;
      }

      const at = this.tileUnder(event);
      const changed =
        (at === null) !== (this.hovered === null) ||
        (at && this.hovered && (at.x !== this.hovered.x || at.y !== this.hovered.y));
      if (changed) {
        this.hovered = at;
        this.callbacks.onHover?.(at);
        this.drawOverlay();
        if (this.painting) this.clearLongPress();
      }
      if (this.painting) this.paintAt(event);
    });

    const end = (event: FederatedPointerEvent): void => {
      this.pointers.delete(event.pointerId);
      // Lifting one finger of a pinch must not resume painting with the other:
      // re-read the gesture if fingers remain, and otherwise finish cleanly.
      this.gesture = this.pointers.size > 1 ? this.readGesture() : null;
      if (this.pointers.size > 0) return;

      // A tap is what is left when a press was not a drag, not a pinch and not
      // a hold.
      if (this.callbacks.onTap && this.pressedAt && !this.dragged && !this.longPressFired) {
        const world = this.world.toLocal({ x: event.global.x, y: event.global.y });
        this.callbacks.onTap(this.tileUnder(event), this.unitNear(world));
      }
      this.pressedAt = null;
      // A finger that lifted is not hovering anywhere; a mouse still is.
      if (event.pointerType !== "mouse" && this.hovered) {
        this.hovered = null;
        this.callbacks.onHover?.(null);
        this.drawOverlay();
      }

      this.clearLongPress();
      if (!this.painting) return;
      this.painting = false;
      this.lastPainted = null;
      this.callbacks.onPaintEnd?.();
    };
    stage.on("pointerup", end);
    stage.on("pointerupoutside", end);

    // Desktop: the wheel zooms about the cursor, which is what every map and
    // every drawing tool does.
    this.app.canvas.addEventListener(
      "wheel",
      (event: WheelEvent) => {
        event.preventDefault();
        const rect = this.app.canvas.getBoundingClientRect();
        const at = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        this.zoomAround(at, event.deltaY < 0 ? 1.12 : 1 / 1.12);
      },
      { passive: false }
    );
  }

  /** A pinch is described by how far apart the fingers are and where they are. */
  private readGesture(): { distance: number; midpoint: Point } | null {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }

  /**
   * Pan and zoom together: the midpoint moving is a pan, the fingers spreading
   * is a zoom, and both happen in the same gesture because that is how hands
   * work.
   */
  private updateGesture(): void {
    const previous = this.gesture;
    const current = this.readGesture();
    if (!previous || !current) return;
    this.gesture = current;

    this.panBy(current.midpoint.x - previous.midpoint.x, current.midpoint.y - previous.midpoint.y);

    if (Math.abs(current.distance - previous.distance) < PINCH_SLOP) return;
    if (previous.distance <= 0) return;
    this.zoomAround(current.midpoint, current.distance / previous.distance);
  }

  private edgeScroll(): void {
    if (!this.painting || !this.paintPoint || !this.callbacks.onPaint || this.pointers.size !== 1)
      return;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const push = (distance: number): number =>
      distance < EDGE_ZONE ? Math.ceil(((EDGE_ZONE - distance) / EDGE_ZONE) * EDGE_SPEED) : 0;
    const p = this.paintPoint;
    const dx = push(w - p.x) - push(p.x);
    const dy = push(h - p.y) - push(p.y);
    if (dx === 0 && dy === 0) return;
    const before = { x: this.world.x, y: this.world.y };
    this.panBy(-dx, -dy);
    if (this.world.x === before.x && this.world.y === before.y) return; // at the edge of the board
    this.paintAt(p);
  }

  /** Forget a half-finished stroke without telling the editor to commit it. */
  private abandonPainting(): void {
    this.clearLongPress();
    this.painting = false;
    this.lastPainted = null;
  }

  /**
   * Paint the tile under the pointer, but only once per tile per drag —
   * otherwise a slow finger toggles the same tile dozens of times a second.
   */
  private paintAt(event: FederatedPointerEvent | Point): void {
    const at = this.tileUnder(event);
    if (!at) return;
    const key = `${at.x},${at.y}`;
    if (key === this.lastPainted) return;
    this.lastPainted = key;
    this.callbacks.onPaint?.(at);
  }

  private clearLongPress(): void {
    if (this.longPressTimer === null) return;
    clearTimeout(this.longPressTimer);
    this.longPressTimer = null;
  }

  private tileUnder(event: FederatedPointerEvent | Point): Coord | null {
    const global = "global" in event ? event.global : event;
    const local = this.world.toLocal(global);
    const at = this.proj.screenToGrid(local.x, local.y);
    if (at.x < 0 || at.y < 0 || at.x >= this.layout.size || at.y >= this.layout.size) return null;
    return at;
  }

  // --- view: pan and zoom --------------------------------------------------

  /**
   * The scale at which the whole diamond just fits. This is the FLOOR, not the
   * default cap: zooming out past it only adds empty space.
   */
  private fitScale(): number {
    const { width: boardW, height: boardH } = this.proj.boardSize(this.layout.size);
    return Math.min(
      (this.app.screen.width * 0.94) / boardW,
      (this.app.screen.height * 0.94) / (boardH + (this.battlefield ? 76 : 0))
    );
  }

  /**
   * Frame the whole board.
   *
   * On a phone in portrait this is width-limited: a 12-wide diamond is twice as
   * wide as it is tall, so fitting the width leaves a band of empty screen above
   * and below. That is not wasted layout — it is why zoom exists. Fit is for
   * seeing the shape of a maze; you zoom in to build one.
   */
  fitToScreen(): void {
    this.framing = "board";
    this.setView(this.fitScale(), null);
  }

  /** Repeat the last automatic framing, e.g. after the screen resized. */
  reframe(): void {
    if (this.framing === "action")
      this.frameAction(this.frameMargin, this.frameInclude, this.frameMinZoom, this.frameMinTilePx);
    else this.fitToScreen();
  }

  /** What the last automatic framing was, so a resize can repeat it. */
  framing: "board" | "action" = "board";

  /**
   * Frame only the part of the board where something is built — the road,
   * the towers and the core — rather than the whole 12×12 diamond.
   *
   * For watching a battle. A tutorial maze uses a corner of the board, and
   * fitting the whole diamond on a phone drew it at about a quarter of the
   * screen with the units two pixels wide. Building still uses the whole board
   * (fitToScreen); watching zooms to the action.
   */
  frameAction(marginTiles = 0, include: readonly Coord[] = [], minZoom = 1, minTilePx = 0): void {
    this.framing = "action";
    this.frameMargin = marginTiles;
    this.frameInclude = include;
    this.frameMinZoom = minZoom;
    this.frameMinTilePx = minTilePx;
    // The bounding box in GRID space first — so a margin means "this many
    // tiles of room to build in", not pixels — then projected to the screen.
    const points: Coord[] = [];
    const take = (x: number, y: number): void => {
      points.push({ x, y });
    };
    for (let y = 0; y < this.layout.size; y++) {
      for (let x = 0; x < this.layout.size; x++) {
        const kind = this.layout.tiles[idx(x, y, this.layout.size)]?.kind ?? "empty";
        if (kind !== "empty") take(x, y);
      }
    }
    for (const at of include) take(at.x, at.y);
    if (!points.length) {
      this.fitToScreen();
      this.framing = "action";
      return;
    }
    const last = this.layout.size - 1;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    // Project occupied cells individually. A grid-space bounding rectangle
    // includes empty corners of L-shaped roads and needlessly shrinks the fight.
    for (const point of points)
      for (const dx of [-marginTiles, marginTiles])
        for (const dy of [-marginTiles, marginTiles]) {
          const x = clamp(point.x + dx, 0, last),
            y = clamp(point.y + dy, 0, last);
          const c = this.proj.gridToScreen(x, y);
          const core = isCore(x, y);
          minX = Math.min(minX, c.x - this.proj.tileW / 2);
          maxX = Math.max(maxX, c.x + this.proj.tileW / 2);
          // Room above for towers, units standing on the road and their bars.
          minY = Math.min(minY, c.y - this.proj.tileH / 2 - (core ? 100 : 58));
          maxY = Math.max(maxY, c.y + this.proj.tileH / 2);
        }
    const pad = 0.93;
    const viewW = this.app.screen.width;
    const viewH = this.app.screen.height;
    // The floor is whichever is bigger: a multiple of the whole-board fit, or
    // a tile a finger can hit (minTilePx CSS px wide).
    const floor = Math.max(this.fitScale() * minZoom, minTilePx / this.proj.tileW);
    const scale = clamp(
      Math.min((viewW * pad) / (maxX - minX), (viewH * pad) / (maxY - minY)),
      floor,
      Math.max(2.4, floor)
    );
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    // Unclamped: a maze built against the edge of the board must still come
    // to the middle of the screen, gate included.
    this.setView(scale, { x: viewW / 2 - cx * scale, y: viewH / 2 - cy * scale }, false);
  }

  private frameMargin = 0;
  private frameMinZoom = 1;
  private frameMinTilePx = 0;
  private frameInclude: readonly Coord[] = [];

  /** Zoom about the centre of the screen, the way a +/- button should behave. */
  zoomBy(factor: number): void {
    const centre = { x: this.app.screen.width / 2, y: this.app.screen.height / 2 };
    this.zoomAround(centre, factor);
  }

  /**
   * Zoom while keeping one screen point pinned to the same board tile.
   *
   * This is what makes pinch-to-zoom feel attached to your fingers rather than
   * to the middle of the screen: the point between them must not drift.
   */
  private zoomAround(screen: Point, factor: number): void {
    const before = this.world.scale.x;
    const after = clamp(before * factor, this.fitScale(), MAX_SCALE);
    if (after === before) return;

    // Keep the board point under `screen` where it is: solve for the new origin.
    const ratio = after / before;
    this.setView(after, {
      x: screen.x - (screen.x - this.world.x) * ratio,
      y: screen.y - (screen.y - this.world.y) * ratio,
    });
  }

  private panBy(dx: number, dy: number): void {
    this.setView(this.world.scale.x, { x: this.world.x + dx, y: this.world.y + dy });
  }

  /**
   * Apply a scale and origin, then pull the board back if it has been dragged
   * off the edge of the screen. Without the clamp a stray flick can leave you
   * looking at nothing, with no way back except the fit button.
   */
  private setView(scale: number, origin: Point | null, keepOnScreen = true): void {
    const { width: boardW, height: boardH } = this.proj.boardSize(this.layout.size);
    this.world.scale.set(scale);

    if (!origin) {
      this.world.x = this.app.screen.width / 2;
      this.world.y =
        (this.app.screen.height - boardH * scale) / 2 +
        (this.proj.tileH / 2 + (this.battlefield ? 30 : 0)) * scale;
      this.onView?.(scale / this.fitScale());
      this.backdrop?.sync(this.app, this.world);
      return;
    }

    if (!keepOnScreen) {
      this.world.x = origin.x;
      this.world.y = origin.y;
      this.onView?.(scale / this.fitScale());
      this.backdrop?.sync(this.app, this.world);
      return;
    }

    // The world origin is the top corner of the diamond, so the board spans
    // half its width either side.
    const halfW = (boardW * scale) / 2;
    const viewW = this.app.screen.width;
    const viewH = this.app.screen.height;
    const boardHeight = boardH * scale;

    // Some slack past the edges, so a maze built in a corner can still be
    // brought to the middle of the screen (frameAction) — and panned there by
    // hand — rather than pinned against the edge of the phone.
    const slackX = viewW * 0.4;
    const slackY = viewH * 0.4;
    // The board spans x ± halfW, so it is wider than the screen when 2·halfW
    // is — comparing halfW alone (as this once did) pinned a zoomed-in board
    // in place, and nothing could scroll it sideways.
    const wide = halfW * 2 > viewW;
    const minX = (wide ? viewW - halfW : halfW) - slackX;
    const maxX = (wide ? halfW : viewW - halfW) + slackX;
    const minY = (boardHeight > viewH ? viewH - boardHeight : 0) - slackY;
    const maxY = (boardHeight > viewH ? 0 : viewH - boardHeight) + slackY;

    this.world.x = clamp(origin.x, Math.min(minX, maxX), Math.max(minX, maxX));
    this.world.y = clamp(origin.y, Math.min(minY, maxY), Math.max(minY, maxY));
    this.onView?.(scale / this.fitScale());
    this.backdrop?.sync(this.app, this.world);
  }

  /** Where a tile's centre is on the page, in CSS pixels. For tests and tooling. */
  screenOf(x: number, y: number): Point {
    const local = this.proj.gridToScreen(x, y);
    const global = this.world.toGlobal(local);
    const rect = this.app.canvas.getBoundingClientRect();
    return { x: rect.left + global.x, y: rect.top + global.y };
  }

  /** Current zoom as a multiple of "the whole board fits". */
  get zoom(): number {
    return this.world.scale.x / this.fitScale();
  }

  /** How many objects the renderer has to draw. Watched by S2-1's budget. */
  get drawableCount(): number {
    return 5 + this.blocks.children.length;
  }

  private backdrop: LandscapeBackdrop | null = null;

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.releasePresentation?.();
    if (import.meta.env.DEV) liveBoards().delete(this);
    this.clearLongPress();
    // Stop the loop BEFORE tearing the renderer down. Otherwise a render
    // already scheduled for this frame runs against a renderer that no longer
    // has a batcher, which throws inside Pixi where nothing can catch it —
    // visible in development every time React mounts a screen twice.
    this.app.ticker?.stop();
    this.battlefield?.dispose();
    this.backdrop?.destroy();
    this.battlefield = null;
    // `{ removeView: true }`, NOT `true`: in Pixi 8, `true` also releases the
    // GLOBAL texture pool every canvas on the page shares. Closing the village
    // behind the tutorial then broke the tutorial board mid-step — it went dark
    // at the gate (friends test #2 prep, Sep 2026).
    this.app.destroy({ removeView: true }, { children: true });
  }
}

/** Boards currently on screen, for browser tests in dev builds (`window.__boards`). */
function liveBoards(): Set<BoardView> {
  const w = window as unknown as { __boards?: Set<BoardView> };
  return (w.__boards ??= new Set());
}

export { BOARD_SIZE, isCore, TILE_W, TILE_H };
